import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth/auth.service';
import { OdooToastService } from 'src/app/services/odoo/odoo-toast.service';
import { PERMISO_ODOO_ESCRIBIR } from 'src/app/services/odoo/odoo.models';
import { conCarga, mensajeDeFallo } from 'src/app/services/odoo/odoo.service';
import {
  ArchivoDia, ClaseArchivo, DiaArchivos, DiaFaltante, ListaDias, OdooArchivosService,
  diaCorto, diaLargo, diaMedio, diaVE, horaEntera, momento, momentoLargo, num, usd,
} from 'src/app/services/odoo/odoo-archivos.service';

export interface FilaClase {
  clase: ClaseArchivo;
  etiqueta: string;
  archivo: ArchivoDia | null;
  /** ok = subido · error = falló la subida · falta = no existe */
  estado: 'ok' | 'error' | 'falta';
  /** estado en 3mit: aún no hay forma de saberlo (null); preparado para cuando exista */
  recibido: 'si' | 'problema' | null;
  resumen: string;
}
export interface TarjetaDia {
  fecha: string;
  titulo: string;
  falta: boolean;
  subidoEn: string;
  subidoISO: string;
  intentos: number;
  filas: FilaClase[];
  archivos: ArchivoDia[];
  /** El día ya tiene algo subido: volver a generar pide motivo */
  yaSubido: boolean;
}
interface VistaLista {
  cargando: boolean;
  error: string | null;
  datos: ListaDias | null;
  tarjetas: TarjetaDia[];
  faltantes: TarjetaDia[];
  aviso: { titulo: string; texto: string } | null;
  subida: string;
}

/**
 * ARCHIVOS DEL DÍA — lista por día (tarjeta por fecha con sus dos archivos), alerta de días faltantes,
 * «Ver» / «Generar y ver» (componente hijo app-odoo-archivo-dia) y diálogo «Regenerar».
 * Todo se arma una vez al llegar los datos (nada se calcula al pintar).
 */
@Component({
  selector: 'app-odoo-archivos',
  templateUrl: './odoo-archivos.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OdooArchivosComponent {
  readonly limite = 10;
  page = 1;
  readonly esperando = [0, 1, 2];
  readonly maxFecha = diaVE(-1);
  fecha = diaVE(-1);

  /** Detalle abierto (null = lista). */
  detalle: { fecha: string; previa: boolean } | null = null;
  version = 0;
  subiendo: string | null = null;
  descargando: string | null = null;

  /** Diálogo «Regenerar». */
  regen: { fecha: string; titulo: string; subidoEn: string } | null = null;
  motivo = '';
  regenerando = false;
  @ViewChild('campoMotivo') campoMotivo?: ElementRef<HTMLTextAreaElement>;

  private readonly pagina$ = new BehaviorSubject<number>(1);
  readonly vm$: Observable<VistaLista> = conCarga(this.pagina$, (p) => this.api.dias(p, this.limite)).pipe(
    map((c) => this.armar(c.cargando, c.error, c.datos)),
  );

  constructor(private api: OdooArchivosService, public _auth: AuthService, private toast: OdooToastService, private cdr: ChangeDetectorRef) {}

  get puedeEditar(): boolean { return this._auth.hasPermission(PERMISO_ODOO_ESCRIBIR); }

  // ── Armado de la vista ──
  private armar(cargando: boolean, error: string | null, d: ListaDias | null): VistaLista {
    if (!d) return { cargando, error, datos: null, tarjetas: [], faltantes: [], aviso: null, subida: '' };
    const faltantes = d.faltantes.map((f) => this.tarjeta(f, true, f));
    const tarjetas = d.dias.map((x) => this.tarjeta(x, false));
    return {
      cargando, error, datos: d, tarjetas, faltantes, aviso: this.aviso(d.faltantes, d.subida_activa),
      subida: d.subida_activa
        ? `Se suben solos cada día a la ${horaEntera(d.hora_subida)}${d.proxima_subida ? ` Próxima subida: ${diaCorto(d.proxima_subida.fecha)}.` : ''}`
        : 'La subida automática está apagada (se enciende en Configuración).',
    };
  }

  private fila(clase: ClaseArchivo, a: ArchivoDia | null, falto: boolean): FilaClase {
    const etiqueta = clase === 'creditos' ? 'Giros' : 'Pagos';
    if (!a) return { clase, etiqueta, archivo: null, estado: 'falta', recibido: null, resumen: falto ? 'No hay archivo de este día' : '—' };
    if (a.estado === 'error') return { clase, etiqueta, archivo: a, estado: 'error', recibido: null, resumen: 'No se pudo subir' };
    const com = typeof a.comercios === 'number' ? ` · ${a.comercios} ${a.comercios === 1 ? 'comercio' : 'comercios'}` : '';
    return {
      clase, etiqueta, archivo: a, estado: 'ok', recibido: a.estado === 'anotado' ? 'si' : a.estado === 'con_problemas' ? 'problema' : null,
      resumen: `${num(a.filas)} ${a.filas === 1 ? 'fila' : 'filas'} · ${usd(a.monto_total_usd)}${com}`,
    };
  }

  private tarjeta(d: DiaArchivos, falta: boolean, f?: DiaFaltante): TarjetaDia {
    const archivos = [d.creditos, d.pagos].filter((a): a is ArchivoDia => !!a && a.estado !== 'error');
    const t = archivos.map((a) => a.subido_en || a.updatedAt || '').filter(Boolean).sort().pop() || '';
    return {
      fecha: d.fecha, titulo: diaLargo(d.fecha), falta, yaSubido: archivos.length > 0,
      subidoEn: t ? `Subido el ${momento(t)}` : '', subidoISO: t, intentos: f ? f.intentos : 0, archivos,
      filas: [this.fila('creditos', d.creditos, falta), this.fila('pagos', d.pagos, falta)],
    };
  }

  private aviso(f: DiaFaltante[], automatica: boolean): { titulo: string; texto: string } | null {
    if (!f.length) return null;
    const reintento = automatica ? ' Se reintenta solo cada hora.' : '';
    if (f.length === 1) {
      const x = f[0];
      const porque = x.motivo === 'error' ? `no se pudo subir${x.error ? `: ${x.error}` : ''}.` : x.motivo === 'incompleto' ? 'solo se subió uno de los dos archivos.' : 'todavía no tiene archivos.';
      return { titulo: 'Falta 1 día por subir.', texto: `El ${diaCorto(x.fecha)}-${x.fecha.slice(0, 4)} ${porque}${reintento}` };
    }
    return { titulo: `Faltan ${f.length} días por subir.`, texto: `${f.map((x) => diaCorto(x.fecha)).join(', ')}.${reintento}` };
  }

  // ── Navegación ──
  recargar(): void { this.pagina$.next(this.page); }
  ir(n: number): void { this.page = n; this.pagina$.next(n); }
  totalPaginas(total: number): number { return Math.max(1, Math.ceil(total / this.limite)); }

  ver(fecha: string): void { this.detalle = { fecha, previa: false }; }
  generarYVer(): void { if (this.fecha && this.fecha <= this.maxFecha) this.detalle = { fecha: this.fecha, previa: true }; }
  volver(): void { this.detalle = null; this.recargar(); }

  // ── Acciones ──
  /** «Subir ahora» / «Subir al buzón»: genera y sube los dos archivos de un día que no estaba subido. */
  subir(fecha: string, yaSubido = false, iso = ''): void {
    if (!this.puedeEditar || this.subiendo) return;
    if (yaSubido) { this.abrirRegenerar(fecha, iso); return; }
    this.subiendo = fecha;
    this.api.subir(fecha).subscribe({
      next: (r) => {
        this.subiendo = null;
        const mal = r.filter((a) => a.estado === 'error').length;
        this.toast.mostrar(mal ? 'No se pudo subir todo: revisa el aviso de arriba.' : 'Listo: se generaron y subieron los archivos del día.', !mal);
        this.version++;
        if (this.detalle) this.detalle = { fecha, previa: false };
        this.recargar();
        this.cdr.markForCheck();
      },
      error: (e: unknown) => { this.subiendo = null; this.toast.mostrar(mensajeDeFallo(e), false); this.cdr.markForCheck(); },
    });
  }

  abrirRegenerar(fecha: string, subidoISO = ''): void {
    if (!this.puedeEditar) return;
    this.motivo = '';
    this.regen = { fecha, titulo: diaMedio(fecha), subidoEn: momentoLargo(subidoISO) };
    this.cdr.markForCheck();
    setTimeout(() => this.campoMotivo && this.campoMotivo.nativeElement.focus());
  }
  cerrarRegenerar(): void { if (!this.regenerando) { this.regen = null; this.cdr.markForCheck(); } }
  @HostListener('document:keydown.escape') alEscape(): void { if (this.regen) this.cerrarRegenerar(); }
  /** Foco atrapado en el diálogo: Tab y Shift+Tab ciclan entre sus controles. */
  atrapar(ev: KeyboardEvent, dialogo: HTMLElement): void {
    if (ev.key !== 'Tab') return;
    const f = Array.from(dialogo.querySelectorAll<HTMLElement>('button:not([disabled]), textarea, input, select, [href]'));
    if (!f.length) return;
    const primero = f[0], ultimo = f[f.length - 1], activo = document.activeElement;
    if (ev.shiftKey && activo === primero) { ev.preventDefault(); ultimo.focus(); }
    else if (!ev.shiftKey && activo === ultimo) { ev.preventDefault(); primero.focus(); }
    else if (!dialogo.contains(activo)) { ev.preventDefault(); primero.focus(); }
  }

  get motivoValido(): boolean { return this.motivo.trim().length >= 3; }

  confirmarRegenerar(): void {
    if (!this.regen || !this.motivoValido || this.regenerando) return;
    const fecha = this.regen.fecha;
    this.regenerando = true;
    this.api.regenerar(fecha, this.motivo.trim()).subscribe({
      next: (r) => {
        this.regenerando = false;
        this.regen = null;
        const mal = r.filter((a) => a.estado === 'error').length;
        this.toast.mostrar(mal ? 'Se regeneró, pero algo no se pudo subir: revisa el aviso.' : 'Listo: se volvieron a generar y subir los archivos.', !mal);
        this.version++;
        this.recargar();
        this.cdr.markForCheck();
      },
      error: (e: unknown) => { this.regenerando = false; this.toast.mostrar(mensajeDeFallo(e), false); this.cdr.markForCheck(); },
    });
  }

  /** Descarga el xlsx de cada archivo del día (uno tras otro). */
  descargarDia(t: TarjetaDia): void {
    if (this.descargando || !t.archivos.length) return;
    this.descargando = t.fecha;
    const siguiente = (i: number): void => {
      if (i >= t.archivos.length) { this.descargando = null; this.cdr.markForCheck(); return; }
      const a = t.archivos[i];
      this.api.descargar(a._id).subscribe({
        next: (blob) => { this.guardar(blob, a.nombre); setTimeout(() => siguiente(i + 1), 250); },
        error: (e: unknown) => { this.descargando = null; this.toast.mostrar(mensajeDeFallo(e), false); this.cdr.markForCheck(); },
      });
    };
    siguiente(0);
  }

  private guardar(blob: Blob, nombre: string): void {
    const url = URL.createObjectURL(blob);
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = nombre;
    enlace.click();
    URL.revokeObjectURL(url);
  }

  porFecha = (_: number, t: TarjetaDia): string => t.fecha;
  porClase = (_: number, f: FilaClase): string => f.clase;
  porIndice = (i: number): number => i;
}
